/**
 * Service-principal client for Openings' machine surface (`/v1/svc/*`).
 *
 * The mobile BFF is a confidential server-side caller, and this module is
 * how it authenticates as one: it signs a short-lived RS256 client assertion
 * with a private key only this deployment holds (RFC 7521/7523
 * `private_key_jwt`), exchanges it at `POST /v1/svc/oauth/token` for a
 * ~600s access token, and calls capability routes with that Bearer token.
 * The private key never leaves the server and nothing here is importable
 * into the mobile binary — the app talks only to this BFF.
 *
 * Why this exists: the settled-payment summary was removed from Openings'
 * public manage route (`manage-appointments-payment-leak.test.ts` over
 * there — a bare customerId is obtainable without OTP, so financial data
 * cannot ride on it). `read_customer_appointments` is the authenticated
 * replacement read, and this module is its only caller.
 *
 * Configuration is three env vars, all required before anything here
 * activates: `OPENINGS_SVC_PRINCIPAL_ID`, `OPENINGS_SVC_KEY_ID`, and
 * `OPENINGS_SVC_PRIVATE_KEY` (PKCS8 PEM; `\n`-escaped newlines accepted, as
 * Vercel env editors tend to store them). Unset means
 * `isOpeningsSvcConfigured()` is false and callers keep their previous
 * behavior — rollout is "provision the principal, set the vars, redeploy",
 * with no code flip.
 */
import crypto from "node:crypto";

import type { OpeningsSvcCustomerAppointmentsResponse } from "./openingsTypes";

const DEFAULT_OPENINGS_API_BASE = "https://api.openings.link";

const getApiBase = () =>
  process.env.OPENINGS_API_BASE?.replace(/\/$/, "") ??
  DEFAULT_OPENINGS_API_BASE;

const getPrincipalId = () => process.env.OPENINGS_SVC_PRINCIPAL_ID?.trim();
const getKeyId = () => process.env.OPENINGS_SVC_KEY_ID?.trim();
const getPrivateKeyPem = () => {
  const raw = process.env.OPENINGS_SVC_PRIVATE_KEY;
  if (!raw) return undefined;
  // Vercel's env editor stores multi-line secrets with literal "\n".
  const pem = raw.includes("-----") ? raw.replace(/\\n/g, "\n") : undefined;
  return pem?.trim() ? pem : undefined;
};

export const isOpeningsSvcConfigured = (): boolean =>
  Boolean(getPrincipalId() && getKeyId() && getPrivateKeyPem());

/**
 * The token endpoint's own URL doubles as the assertion audience — that is
 * what Openings verifies (`aud: env.SVC_TOKEN_ENDPOINT_URL`). Overridable
 * for a deployment whose public base differs from the audience the server
 * was configured with.
 */
const getTokenUrl = () => `${getApiBase()}/v1/svc/oauth/token`;
const getAudience = () =>
  process.env.OPENINGS_SVC_AUDIENCE?.trim() || getTokenUrl();

const base64url = (value: Buffer | string) =>
  Buffer.from(value).toString("base64url");

/**
 * A fresh assertion per token request — never reused, because Openings
 * consumes the `jti` (replay defense) and a second exchange with the same
 * assertion is indistinguishable from an attack.
 *
 * Built with `node:crypto` directly rather than adding a JWT dependency:
 * the shape is three base64url segments and one RS256 signature, and owning
 * those few lines beats a new supply-chain edge on the payment path.
 */
const signClientAssertion = (): string => {
  const principalId = getPrincipalId();
  const keyId = getKeyId();
  const privateKeyPem = getPrivateKeyPem();
  if (!principalId || !keyId || !privateKeyPem) {
    throw new Error("openings svc credentials are not configured");
  }

  const now = Math.floor(Date.now() / 1000);
  const header = { alg: "RS256", typ: "JWT", kid: keyId };
  const claims = {
    iss: principalId,
    sub: principalId,
    aud: getAudience(),
    jti: crypto.randomUUID(),
    iat: now,
    // Openings caps assertion TTL at 120s; 60s leaves clock-skew headroom.
    exp: now + 60,
  };
  const signingInput = `${base64url(JSON.stringify(header))}.${base64url(
    JSON.stringify(claims),
  )}`;
  const signature = crypto
    .createSign("RSA-SHA256")
    .update(signingInput)
    .sign(privateKeyPem);
  return `${signingInput}.${base64url(signature)}`;
};

type CachedToken = { token: string; expiresAtMs: number };

/**
 * Process-local token cache. Refreshing ~60s early keeps a token from dying
 * mid-request; the pending-promise dedup keeps a burst of concurrent Me-tab
 * reads from racing N token exchanges (each of which burns a jti and counts
 * against the token endpoint's 30/min budget).
 *
 * Process-local is CORRECT here, unlike the idempotency store this repo is
 * careful about: a token is a pure credential, so the worst cold-instance
 * outcome is one extra exchange, not a duplicated side effect.
 */
let cachedToken: CachedToken | null = null;
let pendingToken: Promise<string> | null = null;

const TOKEN_REFRESH_MARGIN_MS = 60_000;

/** Test hook — env changes between tests must not see a stale credential. */
export const clearOpeningsSvcTokenCache = () => {
  cachedToken = null;
  pendingToken = null;
};

const fetchAccessToken = async (): Promise<string> => {
  const res = await fetch(getTokenUrl(), {
    method: "POST",
    cache: "no-store",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "client_credentials",
      client_assertion_type:
        "urn:ietf:params:oauth:client-assertion-type:jwt-bearer",
      client_assertion: signClientAssertion(),
    }).toString(),
  });
  const json = await res.json().catch(() => ({}));
  const data = (json as { data?: unknown })?.data as
    | { access_token?: unknown; expires_in?: unknown }
    | undefined;
  if (!res.ok || typeof data?.access_token !== "string") {
    throw new Error(`openings svc token exchange failed (${res.status})`);
  }
  const expiresInSeconds =
    typeof data.expires_in === "number" && data.expires_in > 0
      ? data.expires_in
      : 600;
  cachedToken = {
    token: data.access_token,
    expiresAtMs: Date.now() + expiresInSeconds * 1000,
  };
  return data.access_token;
};

const getAccessToken = async (): Promise<string> => {
  if (
    cachedToken &&
    cachedToken.expiresAtMs - TOKEN_REFRESH_MARGIN_MS > Date.now()
  ) {
    return cachedToken.token;
  }
  if (!pendingToken) {
    pendingToken = fetchAccessToken().finally(() => {
      pendingToken = null;
    });
  }
  return pendingToken;
};

/**
 * `GET /v1/svc/customer-appointments` — the authenticated read carrying the
 * settled-payment summary.
 *
 * `salonId` is passed as the verified-assertion defense the capability
 * documents: Openings derives the owning salon from the customer row either
 * way, and a disagreement is a hard deny. Sending it makes a BFF bug that
 * pairs the wrong business with a customer die loudly instead of read
 * cross-business data.
 *
 * One 401 retry with a fresh token: an access token can be revoked or expire
 * between the cache check and the request landing. Anything else propagates
 * — the CALLER decides whether to degrade (see `getCustomerAppointments`).
 */
export const getSvcCustomerAppointments = async (args: {
  customerId: string;
  businessId: string;
}): Promise<OpeningsSvcCustomerAppointmentsResponse> => {
  const query = new URLSearchParams({
    customerId: args.customerId,
    salonId: args.businessId,
  });
  const url = `${getApiBase()}/v1/svc/customer-appointments?${query.toString()}`;

  const request = async (token: string) =>
    fetch(url, {
      cache: "no-store",
      headers: { Authorization: `Bearer ${token}` },
    });

  let res = await request(await getAccessToken());
  if (res.status === 401) {
    clearOpeningsSvcTokenCache();
    res = await request(await getAccessToken());
  }

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(`openings svc customer-appointments failed (${res.status})`);
  }
  const data = (json as { data?: unknown })?.data;
  const items = (data as { items?: unknown })?.items;
  return { items: Array.isArray(items) ? items : [] };
};
